import { arrayValue, isArray, snapshotArray } from '../array.js';
import type { EducationValue } from '../../../source/shared/education-types.js';
import type * as Core from '../../types/calculation-core.js';
import type { GradeBand, GradeScenario, ScoreConfig, RelativeGradeConfig, ReportCardConfig, GradeAverageConfig, AssessmentConfig, RelativeGradeInput, ReportCardCourse, GradeCourse, GradeAverageInput, AssessmentInput } from './contracts.js';
import { failure, isFiniteNumber, resolveMetadata, success, validateMetadata, } from './types.js';
const VERIFIED_AT = '2026-08-20';
const MAX_CONFIG_NUMBER = 1000000000;
const Z_SCORE_SOURCE = {
    id: 'openstax-standard-normal-z-score',
    title: 'Statistics — 6.1 The Standard Normal Distribution',
    publisher: 'OpenStax',
    url: 'https://openstax.org/books/statistics/pages/6-1-the-standard-normal-distribution',
    verifiedAt: VERIFIED_AT,
};
const WEIGHTED_MEAN_SOURCE = {
    id: 'openstax-weighted-mean',
    title: 'Principles of Finance — 13.1 Measures of Center',
    publisher: 'OpenStax',
    url: 'https://openstax.org/books/principles-finance/pages/13-1-measures-of-center',
    verifiedAt: VERIFIED_AT,
};
const YOK_RULES_DIRECTORY_SOURCE = {
    id: 'yok-mevzuat-directory',
    title: 'Yükseköğretim Kurulu Mevzuat Dizini',
    publisher: 'Yükseköğretim Kurulu',
    url: 'https://www.yok.gov.tr/kurumsal/idari-birimler/hukuk-musavirligi/mevzuat',
    verifiedAt: VERIFIED_AT,
};
function educationMetadata(calculatorId: string, sources: Core.CoreMetadata["sources"], note: string): Core.CoreMetadata {
    return {
        calculatorId,
        version: '1.0.0',
        effectiveFrom: VERIFIED_AT,
        lastVerifiedAt: VERIFIED_AT,
        sources,
        notes: [note],
    };
}
export const DEFAULT_EXAMPLE_ABSOLUTE_GRADE_SCENARIO: GradeScenario = {
    id: 'example-absolute-90-85-80',
    label: 'Örnek mutlak harf tablosu (kurum tablosu değildir)',
    kind: 'example',
    bands: [
        { minimum: 90, label: 'AA', coefficient: 4 },
        { minimum: 85, label: 'BA', coefficient: 3.5 },
        { minimum: 80, label: 'BB', coefficient: 3 },
        { minimum: 75, label: 'CB', coefficient: 2.5 },
        { minimum: 70, label: 'CC', coefficient: 2 },
        { minimum: 65, label: 'DC', coefficient: 1.5 },
        { minimum: 60, label: 'DD', coefficient: 1 },
        { minimum: 0, label: 'FF', coefficient: 0 },
    ],
};
export const DEFAULT_EXAMPLE_RELATIVE_GRADE_SCENARIO: GradeScenario = {
    id: 'example-relative-t-70-65-60',
    label: 'Örnek T-puanı harf tablosu (kurum tablosu değildir)',
    kind: 'example',
    bands: [
        { minimum: 70, label: 'AA', coefficient: 4 },
        { minimum: 65, label: 'BA', coefficient: 3.5 },
        { minimum: 60, label: 'BB', coefficient: 3 },
        { minimum: 55, label: 'CB', coefficient: 2.5 },
        { minimum: 50, label: 'CC', coefficient: 2 },
        { minimum: 45, label: 'DC', coefficient: 1.5 },
        { minimum: 40, label: 'DD', coefficient: 1 },
        { minimum: null, label: 'FF', coefficient: 0 },
    ],
};
export const DEFAULT_EXAMPLE_UNWEIGHTED_BAND_SCENARIO: GradeScenario = {
    id: 'example-unweighted-bands-85-70-50',
    label: 'Örnek genel ortalama bantları (resmî belge kararı değildir)',
    kind: 'example',
    bands: [
        { minimum: 85, label: 'Takdir Bandı', coefficient: null },
        { minimum: 70, label: 'Teşekkür Bandı', coefficient: null },
        { minimum: 50, label: 'Geçer', coefficient: null },
        { minimum: 0, label: 'Başarısız', coefficient: null },
    ],
};
export const DEFAULT_RELATIVE_SYSTEM_GRADE_CONFIG: RelativeGradeConfig = {
    metadata: educationMetadata('egitim-sinav.bagil-sistem-not-hesaplama', [Z_SCORE_SOURCE, YOK_RULES_DIRECTORY_SOURCE], 'z=(x−ortalama)/standart sapma ve T=50+10z matematiği uygulanır. Harf/katsayı tablosu yalnız açık örnek senaryodur; resmî kurum sonucu için uzaktan yönetilen kurum configi gerekir.'),
    maxScore: 100,
    maxStandardDeviation: 100,
    tScoreCenter: 50,
    tScoreScale: 10,
    gradeScenario: DEFAULT_EXAMPLE_RELATIVE_GRADE_SCENARIO,
};
export const DEFAULT_REPORT_CARD_AVERAGE_CONFIG: ReportCardConfig = {
    metadata: educationMetadata('egitim-sinav.karne-notu-hesaplama', [WEIGHTED_MEAN_SOURCE], 'Ders puanlarını haftalık ders saatiyle ağırlıklandıran matematiksel ortalamadır; belge, sınıf geçme veya mezuniyet kararı üretmez.'),
    maxScore: 100,
    maxCourses: 100,
    maxWeeklyHours: 40,
};
export const DEFAULT_GRADE_AVERAGE_CONFIG: GradeAverageConfig = {
    metadata: educationMetadata('egitim-sinav.not-ortalamasi-hesaplama', [WEIGHTED_MEAN_SOURCE, YOK_RULES_DIRECTORY_SOURCE], 'Sayısal ortalama ve tek not karşılaştırması aynı araçta hesaplanır; harf, katsayı ve örnek genel ortalama bantları kurum kuralı değildir ve uzaktan yönetilen config gerektirir.'),
    maxScore: 100,
    maxCourses: 100,
    maxCredits: 10,
    weightedGradeScenario: DEFAULT_EXAMPLE_ABSOLUTE_GRADE_SCENARIO,
    unweightedBandScenario: DEFAULT_EXAMPLE_UNWEIGHTED_BAND_SCENARIO,
};
export const DEFAULT_REQUIRED_FINAL_SCORE_CONFIG: AssessmentConfig = {
    metadata: educationMetadata('egitim-sinav.vize-final-gecme-notu-hesaplama', [WEIGHTED_MEAN_SOURCE, YOK_RULES_DIRECTORY_SOURCE], 'Vize-final toplamı, gerekli final ve kullanıcı barajı karşılaştırması tek araçta ayrı modlardır. Ağırlıklar, hedef ve baraj kullanıcı girdisidir; kurum kararı üretilmez.'),
    maxScore: 100,
    weightTotal: 100,
    weightTolerance: 0.01,
};
function metadataErrors(metadata: Core.CoreMetadata, expectedCalculatorId: string): Core.InputError[] {
    const errors = [...validateMetadata(metadata)];
    if (metadata?.calculatorId !== expectedCalculatorId) {
        errors.push({
            field: 'config.metadata.calculatorId',
            code: 'INVALID_CONFIG',
            message: `Kural metadata kimliği ${expectedCalculatorId} olmalıdır.`,
        });
    }
    return errors;
}
function scoreConfigErrors(config: ScoreConfig, expectedCalculatorId: string): Core.InputError[] {
    const errors = metadataErrors(config.metadata, expectedCalculatorId);
    if (!isFiniteNumber(config.maxScore) || config.maxScore <= 0 || config.maxScore > MAX_CONFIG_NUMBER) {
        errors.push({
            field: 'config.maxScore',
            code: 'INVALID_CONFIG',
            message: 'Azami puan pozitif, sonlu ve güvenli yapılandırma sınırında olmalıdır.',
        });
    }
    return errors;
}
function positiveConfigNumber(value: unknown, field: string, errors: Core.InputError[], options: {
    readonly allowZero?: boolean;
    readonly integer?: boolean;
} = {}) {
    const minimumAccepted = options.allowZero ? 0 : Number.MIN_VALUE;
    if (typeof value !== 'number' || !isFiniteNumber(value)
        || (options.integer && !Number.isSafeInteger(value))
        || value < minimumAccepted
        || value > MAX_CONFIG_NUMBER) {
        errors.push({
            field,
            code: 'INVALID_CONFIG',
            message: `${field} geçerli yapılandırma sınırında ${options.allowZero ? 'negatif olmayan' : 'pozitif'}${options.integer ? ' bir tamsayı' : ' ve sonlu bir sayı'} olmalıdır.`,
        });
    }
}
function scenarioErrors(scenario: GradeScenario, field: string, requireCoefficient: boolean, requireCatchAll: boolean) {
    const errors: Core.InputError[] = [];
    if (!scenario || typeof scenario !== 'object') {
        return { errors: [{ field, code: 'INVALID_CONFIG', message: 'Not senaryosu geçersiz.' }], bands: null };
    }
    if (typeof scenario.id !== 'string' || scenario.id.trim() === '' || scenario.id.length > 120) {
        errors.push({ field: `${field}.id`, code: 'INVALID_CONFIG', message: 'Senaryo kimliği boş olamaz ve 120 karakteri aşamaz.' });
    }
    if (typeof scenario.label !== 'string' || scenario.label.trim() === '' || scenario.label.length > 200) {
        errors.push({ field: `${field}.label`, code: 'INVALID_CONFIG', message: 'Senaryo etiketi boş olamaz ve 200 karakteri aşamaz.' });
    }
    if (scenario.kind !== 'example' && scenario.kind !== 'institution') {
        errors.push({ field: `${field}.kind`, code: 'INVALID_CONFIG', message: 'Senaryo türü example veya institution olmalıdır.' });
    }
    const bandInput = scenario.bands;
    let previousMinimum = Number.POSITIVE_INFINITY;
    const labels = new Set();
    const bands = isArray(bandInput) && bandInput.length > 0 && bandInput.length <= 32 ? snapshotArray(bandInput, (band, index, length) => {
        const bandField = `${field}.bands[${index}]`;
        if (!band || typeof band !== 'object') {
            errors.push({ field: bandField, code: 'INVALID_CONFIG', message: 'Not bandı geçersiz.' });
            return;
        }
        if (typeof band.label !== 'string' || band.label.trim() === '' || band.label.length > 80) {
            errors.push({ field: `${bandField}.label`, code: 'INVALID_CONFIG', message: 'Not bandı etiketi geçersiz.' });
        }
        else if (labels.has(band.label)) {
            errors.push({ field: `${bandField}.label`, code: 'INVALID_CONFIG', message: 'Not bandı etiketleri benzersiz olmalıdır.' });
        }
        else {
            labels.add(band.label);
        }
        if (band.minimum === null) {
            if (index !== length - 1) {
                errors.push({ field: `${bandField}.minimum`, code: 'INVALID_CONFIG', message: 'Alt sınırı olmayan bant yalnız son sırada olabilir.' });
            }
        }
        else if (!isFiniteNumber(band.minimum) || band.minimum >= previousMinimum) {
            errors.push({ field: `${bandField}.minimum`, code: 'INVALID_CONFIG', message: 'Not bandı alt sınırları sonlu ve kesin azalan sırada olmalıdır.' });
        }
        else {
            previousMinimum = band.minimum;
        }
        const coefficient = band.coefficient;
        if (requireCoefficient) {
            if (typeof coefficient !== 'number' || !isFiniteNumber(coefficient) || coefficient < 0 || coefficient > 100) {
                errors.push({ field: `${bandField}.coefficient`, code: 'INVALID_CONFIG', message: 'Her not bandının 0-100 arasında katsayısı olmalıdır.' });
            }
        }
        else if (coefficient !== null && (typeof coefficient !== 'number' || !isFiniteNumber(coefficient) || coefficient < 0 || coefficient > 100)) {
            errors.push({ field: `${bandField}.coefficient`, code: 'INVALID_CONFIG', message: 'Bant katsayısı null veya 0-100 arasında olmalıdır.' });
        }
    }) : null;
    if (!bands) {
        errors.push({ field: `${field}.bands`, code: 'INVALID_CONFIG', message: 'Senaryo 1-32 arasında sıralı bant içermelidir.' });
        return { errors, bands };
    }
    const lastBand = bands[bands.length - 1];
    if (requireCatchAll && lastBand?.minimum !== null) {
        errors.push({ field: `${field}.bands`, code: 'INVALID_CONFIG', message: 'Bağıl not senaryosunun son bandı tüm alt değerleri kapsamalıdır.' });
    }
    if (!requireCatchAll && lastBand?.minimum !== null && lastBand?.minimum !== 0) {
        errors.push({ field: `${field}.bands`, code: 'INVALID_CONFIG', message: '0-100 puan senaryosunun son bandı 0’dan başlamalı veya tüm alt değerleri kapsamalıdır.' });
    }
    return { errors, bands };
}
function boundedScenarioErrors(bands: readonly GradeBand[] | null, field: string, maxScore: number): Core.InputError[] {
    if (!bands)
        return [];
    const errors: Core.InputError[] = [];
    bands.forEach((band: GradeBand, index) => {
        if (band?.minimum !== null && band?.minimum !== undefined && isFiniteNumber(band.minimum)) {
            if (band.minimum < 0 || band.minimum > maxScore) {
                errors.push({
                    field: `${field}.bands[${index}].minimum`,
                    code: 'INVALID_CONFIG',
                    message: `Not bandı alt sınırı 0-${maxScore} puan ölçeğinde olmalıdır.`,
                });
            }
        }
    });
    return errors;
}
function relativeConfigErrors(config: RelativeGradeConfig) {
    const errors = scoreConfigErrors(config, 'egitim-sinav.bagil-sistem-not-hesaplama');
    positiveConfigNumber(config.maxStandardDeviation, 'config.maxStandardDeviation', errors);
    if (!isFiniteNumber(config.tScoreCenter) || Math.abs(config.tScoreCenter) > MAX_CONFIG_NUMBER) {
        errors.push({ field: 'config.tScoreCenter', code: 'INVALID_CONFIG', message: 'T-puanı merkezi sonlu ve güvenli yapılandırma sınırında olmalıdır.' });
    }
    positiveConfigNumber(config.tScoreScale, 'config.tScoreScale', errors);
    const scenario = scenarioErrors(config.gradeScenario, 'config.gradeScenario', true, true);
    errors.push(...scenario.errors);
    return { errors, bands: scenario.bands };
}
function weightedConfigErrors(config: AssessmentConfig, expectedCalculatorId: string): Core.InputError[] {
    const errors = scoreConfigErrors(config, expectedCalculatorId);
    if (config.weightTotal !== 100) {
        errors.push({ field: 'config.weightTotal', code: 'INVALID_CONFIG', message: 'Yüzde ağırlık toplamı 100 olmalıdır.' });
    }
    if (!isFiniteNumber(config.weightTolerance) || config.weightTolerance < 0 || config.weightTolerance > 0.1) {
        errors.push({ field: 'config.weightTolerance', code: 'INVALID_CONFIG', message: 'Ağırlık toleransı 0-0,1 arasında olmalıdır.' });
    }
    return errors;
}
function reportCardConfigErrors(config: ReportCardConfig): Core.InputError[] {
    const errors = scoreConfigErrors(config, 'egitim-sinav.karne-notu-hesaplama');
    positiveConfigNumber(config.maxCourses, 'config.maxCourses', errors, { integer: true });
    positiveConfigNumber(config.maxWeeklyHours, 'config.maxWeeklyHours', errors);
    return errors;
}
function gradeAverageConfigErrors(config: GradeAverageConfig) {
    const errors = scoreConfigErrors(config, 'egitim-sinav.not-ortalamasi-hesaplama');
    positiveConfigNumber(config.maxCourses, 'config.maxCourses', errors, { integer: true });
    positiveConfigNumber(config.maxCredits, 'config.maxCredits', errors);
    const weighted = scenarioErrors(config.weightedGradeScenario, 'config.weightedGradeScenario', true, false);
    const unweighted = scenarioErrors(config.unweightedBandScenario, 'config.unweightedBandScenario', false, false);
    errors.push(...weighted.errors, ...unweighted.errors);
    errors.push(...boundedScenarioErrors(weighted.bands, 'config.weightedGradeScenario', config.maxScore));
    errors.push(...boundedScenarioErrors(unweighted.bands, 'config.unweightedBandScenario', config.maxScore));
    return { errors, weightedBands: weighted.bands, unweightedBands: unweighted.bands };
}
function checkedConfig<T extends ScoreConfig>(config: T, fallback: T, validate: (config: T) => Core.InputError[], invalidMessage: string) {
    const metadata = resolveMetadata(config?.metadata, fallback.metadata);
    if (!config || typeof config !== 'object') {
        return { metadata, errors: [{ field: 'config', code: 'INVALID_CONFIG', message: invalidMessage }] };
    }
    return { metadata, errors: validate(config) };
}
function scoreErrors(value: unknown, field: string, maxScore: number): Core.InputError[] {
    if (value === null || value === undefined) {
        return [{ field, code: 'REQUIRED', message: `${field} gereklidir.` }];
    }
    if (typeof value !== 'number' || !isFiniteNumber(value)) {
        return [{ field, code: 'NOT_FINITE', message: `${field} sonlu bir sayı olmalıdır.` }];
    }
    if (value < 0 || value > maxScore) {
        return [{ field, code: 'OUT_OF_RANGE', message: `${field} 0-${maxScore} aralığında olmalıdır.` }];
    }
    return [];
}
function percentageErrors(value: unknown, field: string): Core.InputError[] {
    return scoreErrors(value, field, 100);
}
function validateWeightTotal(first: number, second: number, firstField: string, secondField: string, config: AssessmentConfig): Core.InputError[] {
    if (Math.abs(first + second - config.weightTotal) <= config.weightTolerance)
        return [];
    const message = `Yüzde ağırlıkların toplamı ${config.weightTotal} olmalıdır.`;
    return [
        { field: firstField, code: 'OUT_OF_RANGE', message },
        { field: secondField, code: 'OUT_OF_RANGE', message },
    ];
}
function scenarioReference(scenario: GradeScenario) {
    return { id: scenario.id, label: scenario.label, kind: scenario.kind };
}
function matchGrade(value: number, bands: readonly GradeBand[]) {
    const band = bands.find((candidate) => candidate.minimum === null || value >= candidate.minimum)
        ?? bands[bands.length - 1];
    return { label: band.label, coefficient: band.coefficient, minimum: band.minimum };
}
function finiteResult(values: readonly number[]) {
    return values.every(Number.isFinite);
}
export function calculateRelativeSystemGrade(input: Readonly<Record<string, unknown>>, config: RelativeGradeConfig = DEFAULT_RELATIVE_SYSTEM_GRADE_CONFIG): Core.Calculation<EducationValue> {
    const metadata = resolveMetadata(config?.metadata, DEFAULT_RELATIVE_SYSTEM_GRADE_CONFIG.metadata);
    if (!config || typeof config !== 'object')
        return failure([{ field: 'config', code: 'INVALID_CONFIG', message: 'Bağıl sistem konfigürasyonu geçersiz.' }], metadata);
    const checked = { metadata, ...relativeConfigErrors(config) };
    if (checked.errors.length > 0 || !checked.bands)
        return failure(checked.errors, checked.metadata);
    if (!input || typeof input !== 'object') {
        return failure([{ field: 'input', code: 'REQUIRED', message: 'Bağıl sistem girdileri gereklidir.' }], checked.metadata);
    }
    const score = input.score;
    const errors = scoreErrors(score, 'score', config.maxScore);
    const groupMean = input.groupMean;
    errors.push(...scoreErrors(groupMean, 'groupMean', config.maxScore));
    const standardDeviation = input.standardDeviation;
    if (standardDeviation === null || standardDeviation === undefined) {
        errors.push({ field: 'standardDeviation', code: 'REQUIRED', message: 'standardDeviation gereklidir.' });
    }
    else if (typeof standardDeviation !== 'number' || !isFiniteNumber(standardDeviation)) {
        errors.push({ field: 'standardDeviation', code: 'NOT_FINITE', message: 'standardDeviation sonlu bir sayı olmalıdır.' });
    }
    else if (standardDeviation === 0) {
        errors.push({ field: 'standardDeviation', code: 'DIVISION_BY_ZERO', message: 'Standart sapma sıfır olamaz.' });
    }
    else if (standardDeviation < 0 || standardDeviation > config.maxStandardDeviation) {
        errors.push({ field: 'standardDeviation', code: 'OUT_OF_RANGE', message: 'Standart sapma izin verilen pozitif aralığın dışındadır.' });
    }
    if (errors.length > 0 || typeof score !== 'number' || !isFiniteNumber(score) || typeof groupMean !== 'number' || !isFiniteNumber(groupMean) || typeof standardDeviation !== 'number' || !isFiniteNumber(standardDeviation))
        return failure(errors, checked.metadata);
    const zScore = (score - groupMean) / standardDeviation;
    const tScore = config.tScoreCenter + config.tScoreScale * zScore;
    if (!finiteResult([zScore, tScore])) {
        return failure([{ field: 'result', code: 'CALCULATION_OVERFLOW', message: 'Bağıl puan temsil edilebilir sayı aralığının dışında.' }], checked.metadata);
    }
    return success({
        ...input,
        zScore,
        tScore,
        grade: matchGrade(tScore, checked.bands),
        scenario: scenarioReference(config.gradeScenario),
    }, checked.metadata);
}
function isRecord(value: unknown): value is Readonly<Record<string, unknown>> {
    return value !== null && typeof value === 'object';
}
function courseName(value: unknown, index: number, errors: Core.InputError[]) {
    if (value === undefined || value === null || value === '')
        return `Ders ${index + 1}`;
    if (typeof value !== 'string' || value.length > 120) {
        errors.push({ field: `courses[${index}].name`, code: 'UNSUPPORTED_VALUE', message: 'Ders adı metin olmalı ve 120 karakteri aşmamalıdır.' });
        return `Ders ${index + 1}`;
    }
    const normalized = value.trim();
    return normalized === '' ? `Ders ${index + 1}` : normalized;
}
export function calculateReportCardAverage(input: Readonly<Record<string, unknown>>, config: ReportCardConfig = DEFAULT_REPORT_CARD_AVERAGE_CONFIG): Core.Calculation<EducationValue> {
    const checked = checkedConfig(config, DEFAULT_REPORT_CARD_AVERAGE_CONFIG, reportCardConfigErrors, 'Karne ortalaması konfigürasyonu geçersiz.');
    if (checked.errors.length > 0)
        return failure(checked.errors, checked.metadata);
    const rows = arrayValue(input && typeof input === 'object' ? input.courses : undefined);
    if (rows === null || rows.length === 0) {
        return failure([{ field: 'courses', code: 'REQUIRED', message: 'En az bir ders gereklidir.' }], checked.metadata);
    }
    if (rows.length > config.maxCourses) {
        return failure([{ field: 'courses', code: 'OUT_OF_RANGE', message: `En fazla ${config.maxCourses} ders hesaplanabilir.` }], checked.metadata);
    }
    const errors: Core.InputError[] = [];
    const courses: { name: string; score: number; weeklyHours: number; weightedPoints: number }[] = [];
    const checkedRows = snapshotArray(rows, (course, index) => {
        if (!isRecord(course)) {
            errors.push({ field: `courses[${index}]`, code: 'REQUIRED', message: 'Ders kaydı geçersiz.' });
            return;
        }
        const name = courseName(course.name, index, errors);
        const score = course.score;
        errors.push(...scoreErrors(score, `courses[${index}].score`, config.maxScore));
        const weeklyHours = course.weeklyHours;
        if (weeklyHours === null || weeklyHours === undefined) {
            errors.push({ field: `courses[${index}].weeklyHours`, code: 'REQUIRED', message: 'Haftalık ders saati gereklidir.' });
        }
        else if (typeof weeklyHours !== 'number' || !isFiniteNumber(weeklyHours)) {
            errors.push({ field: `courses[${index}].weeklyHours`, code: 'NOT_FINITE', message: 'Haftalık ders saati sonlu olmalıdır.' });
        }
        else if (weeklyHours <= 0 || weeklyHours > config.maxWeeklyHours) {
            errors.push({ field: `courses[${index}].weeklyHours`, code: 'OUT_OF_RANGE', message: 'Haftalık ders saati izin verilen pozitif aralığın dışındadır.' });
        }
        if (typeof score === 'number' && isFiniteNumber(score) && typeof weeklyHours === 'number' && isFiniteNumber(weeklyHours)) {
            courses.push({ name, score, weeklyHours, weightedPoints: score * weeklyHours });
        }
    });
    if (!checkedRows)
        return failure([{ field: 'courses', code: 'REQUIRED', message: 'Ders kaydı geçersiz.' }], checked.metadata);
    if (errors.length > 0)
        return failure(errors, checked.metadata);
    const totalWeeklyHours = courses.reduce((total, course) => total + course.weeklyHours, 0);
    const weightedPointTotal = courses.reduce((total, course) => total + course.weightedPoints, 0);
    const weightedAverage = weightedPointTotal / totalWeeklyHours;
    if (!finiteResult([totalWeeklyHours, weightedPointTotal, weightedAverage])) {
        return failure([{ field: 'result', code: 'CALCULATION_OVERFLOW', message: 'Karne ortalaması temsil edilebilir sayı aralığının dışında.' }], checked.metadata);
    }
    return success({ courses, courseCount: courses.length, totalWeeklyHours, weightedPointTotal, weightedAverage }, checked.metadata);
}
export function calculateGradeAverage(input: Readonly<Record<string, unknown>>, config: GradeAverageConfig = DEFAULT_GRADE_AVERAGE_CONFIG): Core.Calculation<EducationValue> {
    const metadata = resolveMetadata(config?.metadata, DEFAULT_GRADE_AVERAGE_CONFIG.metadata);
    if (!config || typeof config !== 'object')
        return failure([{ field: 'config', code: 'INVALID_CONFIG', message: 'Not ortalaması konfigürasyonu geçersiz.' }], metadata);
    const checked = { metadata, ...gradeAverageConfigErrors(config) };
    if (checked.errors.length > 0 || !checked.weightedBands || !checked.unweightedBands)
        return failure(checked.errors, checked.metadata);
    const { weightedBands, unweightedBands } = checked;
    if (!input || typeof input !== 'object') {
        return failure([{ field: 'input', code: 'REQUIRED', message: 'Not ortalaması girdileri gereklidir.' }], checked.metadata);
    }
    const mode = input.mode;
    if (mode !== 'average' && mode !== 'single-grade') {
        return failure([{ field: 'mode', code: 'UNSUPPORTED_VALUE', message: 'Not ortalaması modu desteklenmiyor.' }], checked.metadata);
    }
    if (mode === 'single-grade') {
        const score = input.score;
        const errors = scoreErrors(score, 'score', config.maxScore);
        const threshold = input.threshold;
        errors.push(...scoreErrors(threshold, 'threshold', config.maxScore));
        if (errors.length > 0 || typeof score !== 'number' || !isFiniteNumber(score) || typeof threshold !== 'number' || !isFiniteNumber(threshold))
            return failure(errors, checked.metadata);
        const differenceFromThreshold = score - threshold;
        return success({
            mode,
            averageMode: null,
            courses: null,
            courseCount: null,
            totalWeight: null,
            weightedPointTotal: null,
            average100: null,
            gradePointAverage: null,
            score: score,
            threshold: threshold,
            differenceFromThreshold,
            meetsThreshold: differenceFromThreshold >= 0,
            classification: matchGrade(score, weightedBands),
            scenario: scenarioReference(config.weightedGradeScenario),
        }, checked.metadata);
    }
    const averageMode = input.averageMode;
    if (averageMode !== 'weighted' && averageMode !== 'unweighted') {
        return failure([{ field: 'averageMode', code: 'UNSUPPORTED_VALUE', message: 'Ortalama türü desteklenmiyor.' }], checked.metadata);
    }
    const rows = arrayValue(input.courses);
    if (rows === null || rows.length === 0) {
        return failure([{ field: 'courses', code: 'REQUIRED', message: 'En az bir ders gereklidir.' }], checked.metadata);
    }
    if (rows.length > config.maxCourses) {
        return failure([{ field: 'courses', code: 'OUT_OF_RANGE', message: `En fazla ${config.maxCourses} ders hesaplanabilir.` }], checked.metadata);
    }
    const errors: Core.InputError[] = [];
    const courses: { name: string; score: number; weight: number; weightedPoints: number; grade: ReturnType<typeof matchGrade> | null }[] = [];
    const checkedRows = snapshotArray(rows, (course, index) => {
        if (!isRecord(course)) {
            errors.push({ field: `courses[${index}]`, code: 'REQUIRED', message: 'Ders kaydı geçersiz.' });
            return;
        }
        const name = courseName(course.name, index, errors);
        const score = course.score;
        errors.push(...scoreErrors(score, `courses[${index}].score`, config.maxScore));
        let weight = 1;
        if (averageMode === 'weighted') {
            const credits = course.credits;
            if (credits === null || credits === undefined) {
                errors.push({ field: `courses[${index}].credits`, code: 'REQUIRED', message: 'Kredili modda ders kredisi gereklidir.' });
            }
            else if (typeof credits !== 'number' || !isFiniteNumber(credits)) {
                errors.push({ field: `courses[${index}].credits`, code: 'NOT_FINITE', message: 'Ders kredisi sonlu olmalıdır.' });
            }
            else if (credits <= 0 || credits > config.maxCredits) {
                errors.push({ field: `courses[${index}].credits`, code: 'OUT_OF_RANGE', message: 'Ders kredisi izin verilen pozitif aralığın dışındadır.' });
            }
            else {
                weight = credits;
            }
        }
        if (typeof score === 'number' && isFiniteNumber(score)) {
            const grade = averageMode === 'weighted' ? matchGrade(score, weightedBands) : null;
            courses.push({ name, score, weight, weightedPoints: score * weight, grade });
        }
    });
    if (!checkedRows)
        return failure([{ field: 'courses', code: 'REQUIRED', message: 'Ders kaydı geçersiz.' }], checked.metadata);
    if (errors.length > 0)
        return failure(errors, checked.metadata);
    const totalWeight = courses.reduce((total, course) => total + course.weight, 0);
    const weightedPointTotal = courses.reduce((total, course) => total + course.weightedPoints, 0);
    const average100 = weightedPointTotal / totalWeight;
    const selectedScenario = averageMode === 'weighted' ? config.weightedGradeScenario : config.unweightedBandScenario;
    let gradePointAverage = null;
    if (averageMode === 'weighted') {
        const gradePointTotal = courses.reduce((total, course) => total + (course.grade?.coefficient ?? 0) * course.weight, 0);
        gradePointAverage = gradePointTotal / totalWeight;
    }
    const numericResults = gradePointAverage === null
        ? [totalWeight, weightedPointTotal, average100]
        : [totalWeight, weightedPointTotal, average100, gradePointAverage];
    if (!finiteResult(numericResults)) {
        return failure([{ field: 'result', code: 'CALCULATION_OVERFLOW', message: 'Not ortalaması temsil edilebilir sayı aralığının dışında.' }], checked.metadata);
    }
    return success({
        mode,
        averageMode,
        courses,
        courseCount: courses.length,
        totalWeight,
        weightedPointTotal,
        average100,
        gradePointAverage,
        score: null,
        threshold: null,
        differenceFromThreshold: null,
        meetsThreshold: null,
        classification: matchGrade(average100, averageMode === 'weighted' ? weightedBands : unweightedBands),
        scenario: scenarioReference(selectedScenario),
    }, checked.metadata);
}
export function calculateRequiredFinalScore(input: Readonly<Record<string, unknown>>, config: AssessmentConfig = DEFAULT_REQUIRED_FINAL_SCORE_CONFIG): Core.Calculation<EducationValue> {
    const checked = checkedConfig(config, DEFAULT_REQUIRED_FINAL_SCORE_CONFIG, (candidate) => weightedConfigErrors(candidate, 'egitim-sinav.vize-final-gecme-notu-hesaplama'), 'Vize-final hedef konfigürasyonu geçersiz.');
    if (checked.errors.length > 0)
        return failure(checked.errors, checked.metadata);
    if (!input || typeof input !== 'object') {
        return failure([{ field: 'input', code: 'REQUIRED', message: 'Vize-final hedef girdileri gereklidir.' }], checked.metadata);
    }
    const mode = input.mode;
    const emptyOutput = {
        observedMidtermScore: null,
        observedFinalScore: null,
        observedMidtermWeightPercent: null,
        observedFinalWeightPercent: null,
        observedMidtermContribution: null,
        observedFinalContribution: null,
        weightedTotal: null,
        weightTotal: null,
        midtermScore: null,
        targetAverage: null,
        midtermWeightPercent: null,
        finalWeightPercent: null,
        midtermContribution: null,
        requiredFinalScore: null,
        status: null,
        thresholdMode: null,
        examScore: null,
        threshold: null,
        priorScore: null,
        priorWeightPercent: null,
        examWeightPercent: null,
        evaluatedScore: null,
        differenceFromThreshold: null,
        meetsThreshold: null,
    };
    if (mode !== 'weighted-total' && mode !== 'required-final' && mode !== 'threshold-check') {
        return failure([{ field: 'mode', code: 'UNSUPPORTED_VALUE', message: 'Vize-final hesaplama modu desteklenmiyor.' }], checked.metadata);
    }
    if (mode === 'weighted-total') {
        const observedMidtermScore = input.observedMidtermScore;
        const errors = scoreErrors(observedMidtermScore, 'observedMidtermScore', config.maxScore);
        const observedFinalScore = input.observedFinalScore;
        errors.push(...scoreErrors(observedFinalScore, 'observedFinalScore', config.maxScore));
        const observedMidtermWeightPercent = input.observedMidtermWeightPercent;
        errors.push(...percentageErrors(observedMidtermWeightPercent, 'observedMidtermWeightPercent'));
        const observedFinalWeightPercent = input.observedFinalWeightPercent;
        errors.push(...percentageErrors(observedFinalWeightPercent, 'observedFinalWeightPercent'));
        if (errors.length === 0 && typeof observedMidtermWeightPercent === 'number' && isFiniteNumber(observedMidtermWeightPercent) && typeof observedFinalWeightPercent === 'number' && isFiniteNumber(observedFinalWeightPercent))
            errors.push(...validateWeightTotal(observedMidtermWeightPercent, observedFinalWeightPercent, 'observedMidtermWeightPercent', 'observedFinalWeightPercent', config));
        if (errors.length > 0 || typeof observedMidtermScore !== 'number' || !isFiniteNumber(observedMidtermScore) || typeof observedFinalScore !== 'number' || !isFiniteNumber(observedFinalScore) || typeof observedMidtermWeightPercent !== 'number' || !isFiniteNumber(observedMidtermWeightPercent) || typeof observedFinalWeightPercent !== 'number' || !isFiniteNumber(observedFinalWeightPercent))
            return failure(errors, checked.metadata);
        const observedMidtermContribution = observedMidtermScore * observedMidtermWeightPercent / 100;
        const observedFinalContribution = observedFinalScore * observedFinalWeightPercent / 100;
        const weightedTotal = observedMidtermContribution + observedFinalContribution;
        if (!finiteResult([observedMidtermContribution, observedFinalContribution, weightedTotal])) {
            return failure([{ field: 'result', code: 'CALCULATION_OVERFLOW', message: 'Ağırlıklı toplam temsil edilebilir sayı aralığının dışında.' }], checked.metadata);
        }
        return success({
            ...emptyOutput,
            mode,
            observedMidtermScore: observedMidtermScore,
            observedFinalScore: observedFinalScore,
            observedMidtermWeightPercent: observedMidtermWeightPercent,
            observedFinalWeightPercent: observedFinalWeightPercent,
            observedMidtermContribution,
            observedFinalContribution,
            weightedTotal,
            weightTotal: observedMidtermWeightPercent + observedFinalWeightPercent,
        }, checked.metadata);
    }
    if (mode === 'threshold-check') {
        const examScore = input.examScore;
        const errors = scoreErrors(examScore, 'examScore', config.maxScore);
        const threshold = input.threshold;
        errors.push(...scoreErrors(threshold, 'threshold', config.maxScore));
        const { priorScore, priorWeightPercent: priorWeightInput } = input;
        const hasPriorScore = priorScore !== undefined && priorScore !== null;
        const hasPriorWeight = priorWeightInput !== undefined && priorWeightInput !== null;
        if (hasPriorScore !== hasPriorWeight) {
            errors.push({
                field: hasPriorScore ? 'priorWeightPercent' : 'priorScore',
                code: 'REQUIRED',
                message: 'Ağırlıklı baraj kontrolünde önceki not ve ağırlığı birlikte girilmelidir.',
            });
        }
        if (hasPriorScore)
            errors.push(...scoreErrors(priorScore, 'priorScore', config.maxScore));
        if (hasPriorWeight)
            errors.push(...percentageErrors(priorWeightInput, 'priorWeightPercent'));
        if (errors.length > 0 || typeof examScore !== 'number' || !isFiniteNumber(examScore) || typeof threshold !== 'number' || !isFiniteNumber(threshold))
            return failure(errors, checked.metadata);
        const thresholdMode = hasPriorScore ? 'weighted' : 'direct';
        const priorWeightPercent = thresholdMode === 'weighted' && typeof priorWeightInput === 'number' && isFiniteNumber(priorWeightInput) ? priorWeightInput : 0;
        const examWeightPercent = 100 - priorWeightPercent;
        const evaluatedScore = thresholdMode === 'weighted'
            ? (typeof priorScore === 'number' && isFiniteNumber(priorScore) ? priorScore : Number.NaN) * priorWeightPercent / 100 + examScore * examWeightPercent / 100
            : examScore;
        const differenceFromThreshold = evaluatedScore - threshold;
        if (!finiteResult([evaluatedScore, differenceFromThreshold])) {
            return failure([{ field: 'result', code: 'CALCULATION_OVERFLOW', message: 'Değerlendirilen puan temsil edilebilir sayı aralığının dışında.' }], checked.metadata);
        }
        return success({
            ...emptyOutput,
            mode,
            thresholdMode,
            examScore: examScore,
            threshold: threshold,
            priorScore: thresholdMode === 'weighted' ? priorScore : null,
            priorWeightPercent,
            examWeightPercent,
            evaluatedScore,
            differenceFromThreshold,
            meetsThreshold: differenceFromThreshold >= 0,
        }, checked.metadata);
    }
    const midtermScore = input.midtermScore;
    const errors = scoreErrors(midtermScore, 'midtermScore', config.maxScore);
    const targetAverage = input.targetAverage;
    errors.push(...scoreErrors(targetAverage, 'targetAverage', config.maxScore));
    const midtermWeightPercent = input.midtermWeightPercent;
    errors.push(...percentageErrors(midtermWeightPercent, 'midtermWeightPercent'));
    const finalWeightPercent = input.finalWeightPercent;
    errors.push(...percentageErrors(finalWeightPercent, 'finalWeightPercent'));
    if (typeof finalWeightPercent === 'number' && isFiniteNumber(finalWeightPercent) && finalWeightPercent === 0) {
        errors.push({ field: 'finalWeightPercent', code: 'DIVISION_BY_ZERO', message: 'Gerekli final hesabında final ağırlığı sıfır olamaz.' });
    }
    if (errors.length === 0 && typeof midtermWeightPercent === 'number' && isFiniteNumber(midtermWeightPercent) && typeof finalWeightPercent === 'number' && isFiniteNumber(finalWeightPercent)) {
        errors.push(...validateWeightTotal(midtermWeightPercent, finalWeightPercent, 'midtermWeightPercent', 'finalWeightPercent', config));
    }
    if (errors.length > 0 || typeof midtermScore !== 'number' || !isFiniteNumber(midtermScore) || typeof targetAverage !== 'number' || !isFiniteNumber(targetAverage) || typeof midtermWeightPercent !== 'number' || !isFiniteNumber(midtermWeightPercent) || typeof finalWeightPercent !== 'number' || !isFiniteNumber(finalWeightPercent))
        return failure(errors, checked.metadata);
    const midtermContribution = midtermScore * midtermWeightPercent / 100;
    const requiredFinalScore = (targetAverage - midtermContribution) / (finalWeightPercent / 100);
    if (!finiteResult([midtermContribution, requiredFinalScore])) {
        return failure([{ field: 'result', code: 'CALCULATION_OVERFLOW', message: 'Gerekli final puanı temsil edilebilir sayı aralığının dışında.' }], checked.metadata);
    }
    const status = requiredFinalScore <= 0
        ? 'already-met'
        : requiredFinalScore > config.maxScore
            ? 'unreachable'
            : 'reachable';
    return success({
        ...emptyOutput,
        mode,
        midtermScore: midtermScore,
        targetAverage: targetAverage,
        midtermWeightPercent: midtermWeightPercent,
        finalWeightPercent: finalWeightPercent,
        midtermContribution,
        requiredFinalScore,
        status,
    }, checked.metadata);
}
